import { useState } from 'react';

import { Button } from '@/ux-common/components/button/button';
import { Calendar } from '@/ux-common/components/calendar/calendar';
import { cn } from '@/ux-common/lib/utils';

import { MODE_VALIDATORS } from './calendar-picker.constants';
import type { CalendarPickerProps } from './calendar-picker.types';

/**
 * CalendarPicker — staging container around `Calendar` with Cancel/Apply
 * actions. Maintains a `pendingValue` internally and only commits via
 * `onConfirm`. Cancel resets to `defaultValue` and fires `onCancel`.
 *
 * Visual styling is delivered through CSS classes:
 * - `.calendar-picker` (auto-applied) — flex column container, default md sizing
 * - `.calendar-picker-{sm|md|lg}` (consumer-composed via `className`) sizes
 *   the inner Calendar via descendant selectors. No prop forwarding required.
 * - `.calendar-picker-actions` — right-aligned action row
 *
 * Composes well inside a Radix `Popover` triggered by `DropdownButton` for
 * the standard date-picker UX:
 *
 * ```tsx
 * <Popover.Root open={open} onOpenChange={setOpen}>
 *   <Popover.Trigger asChild>
 *     <DropdownButton className="btn-tier3">
 *       {date ? formatDate(date) : 'Pick a date'}
 *     </DropdownButton>
 *   </Popover.Trigger>
 *   <Popover.Portal>
 *     <Popover.Content sideOffset={4}>
 *       <CalendarPicker
 *         mode="single"
 *         className="calendar-picker-sm"
 *         defaultValue={date}
 *         onConfirm={(d) => { setDate(d); setOpen(false); }}
 *         onCancel={() => setOpen(false)}
 *       />
 *     </Popover.Content>
 *   </Popover.Portal>
 * </Popover.Root>
 * ```
 */
export const CalendarPicker = (props: CalendarPickerProps) => {
  const { mode, onCancel, onConfirm, defaultValue, timeZone, localeCode, className, ...rest } =
    props;

  const [pendingValue, setPendingValue] = useState(defaultValue);

  const handleConfirm = () => {
    const validator = MODE_VALIDATORS[mode];
    if (pendingValue && validator(pendingValue)) {
      onConfirm(pendingValue as never);
    }
  };

  const handleCancel = () => {
    setPendingValue(defaultValue);
    onCancel();
  };

  const isApplyDisabled = !pendingValue || !MODE_VALIDATORS[mode](pendingValue);

  return (
    <div data-component="CalendarPicker" className={cn('calendar-picker', className)}>
      <Calendar
        mode={mode}
        selected={pendingValue as never}
        onSelect={setPendingValue as never}
        timeZone={timeZone}
        localeCode={localeCode}
        {...rest}
      />
      <div className="calendar-picker-actions">
        <Button className="calendar-picker-cancel" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          className="btn-inverse calendar-picker-apply"
          onClick={handleConfirm}
          disabled={isApplyDisabled}
        >
          Apply
        </Button>
      </div>
    </div>
  );
};

CalendarPicker.displayName = 'CalendarPicker';
